import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { ltrDir, rtlDir } from '../constants';
import { black, white } from '../../../styles/colors';
import arrowTop from '../../../assets/images/tooltip-arrow-top.svg';
import arrowRight from '../../../assets/images/tooltip-arrow-right.svg';
import arrowBottom from '../../../assets/images/tooltip-arrow-bottom.svg';
import arrowLeft from '../../../assets/images/tooltip-arrow-left.svg';
import { appSelectors } from '../reducer/app-reducer';

// the maximum opacity the tooltip gets
const tipOpacity = '0.85';

// the size along which it points
const arrowPointingLength = 8;

// the size along the base of the arrowhead
const arrowBaseLength = 16;

// the distance the tooltip will have from the tipped element
const tipDistance = 3;

// how long to fade in or out
const fadeSeconds = 0.4;

let isRtl = false;

const TooltipDiv = styled.div((props) => {
  let style = {
    display: 'block',
    '& div': {
      boxSizing: 'border-box'
    },
    position: 'absolute',
    opacity: '0',
    zIndex: 1,
    transition: 'opacity ' + fadeSeconds + 's ease'
  };

  // padding for the arrow
  switch (props.tipPosition) {
    case 'top':
      // arrow on bottom
      style.padding = '0px 0px ' + arrowPointingLength + 'px 0px';
      break;
    case 'right':
      // arrow on the left
      style.padding = '0px 0px 0px ' + arrowPointingLength + 'px ';
      break;
    case 'bottom':
      // arrow on top
      style.padding = arrowPointingLength + 'px 0px 0px 0px';
      break;
    default:
    case 'left':
      // arrow on right
      style.padding = '0px ' + arrowPointingLength + 'px 0px 0px';
      break;
  }
  if (!props.fadeTip) {
    style.opacity = tipOpacity;
  }
  // once we have the size we can position it and draw it (make it opaque)
  if (props.tipRect) {
    // these set the tooltip in a centered position vis-a-vis the tipped element
    if (props.tipPosition === 'top' || props.tipPosition === 'bottom') {
      const xTippedCenter = (props.tippedRect.left + props.tippedRect.right) / 2;
      const xTipOffset = props.tipRect.width / 2;
      let xTipLeft = xTippedCenter - xTipOffset;
      xTipLeft += window.pageXOffset;
      xTipLeft = Math.floor(xTipLeft);
      style.left = xTipLeft + 'px';
    } else {
      const yTippedCenter = (props.tippedRect.top + props.tippedRect.bottom) / 2;
      const yTipOffset = props.tipRect.height / 2;
      let yTipTop = yTippedCenter - yTipOffset;
      yTipTop += window.pageYOffset;
      yTipTop = Math.floor(yTipTop);
      style.top = yTipTop + 'px';
    }

    // these put it so the arrow is right next to the near edge of the tipped element
    switch (props.tipPosition) {
      case 'top':
        style.top = Math.floor(props.tippedRect.top - (props.tipRect.height + tipDistance)) + window.pageYOffset + 'px';
        break;
      case 'right':
        style.left = Math.floor(props.tippedRect.right + tipDistance) + window.pageXOffset + 'px';
        break;
      case 'bottom':
        style.top = Math.floor(props.tippedRect.bottom + window.pageYOffset + tipDistance) + 'px';
        break;
      default:
      case 'left':
        style.left =
          Math.floor(props.tippedRect.left - (props.tipRect.width + tipDistance)) + window.pageXOffset + 'px';
        break;
    }
  }
  return style;
});

const TooltipArrow = styled.div(() => {
  let style = {
    display: 'block',
    position: 'absolute',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100%'
  };
  return style;
});

const TooltipArrowRight = styled(TooltipArrow)(() => {
  let style = {
    right: '0px',
    top: '50%',
    marginTop: -arrowBaseLength / 2 + 'px',
    width: arrowPointingLength + 'px',
    height: arrowBaseLength + 'px',
    backgroundImage: 'url(' + arrowRight + ')'
  };
  return style;
});

const TooltipArrowLeft = styled(TooltipArrow)(() => {
  let style = {
    left: '0px',
    right: 'auto',
    top: '50%',
    marginTop: -arrowBaseLength / 2 + 'px',
    width: arrowPointingLength + 'px',
    height: arrowBaseLength + 'px',
    backgroundImage: 'url(' + arrowLeft + ')'
  };
  return style;
});

const TooltipArrowTop = styled(TooltipArrow)((props) => {
  let style = {
    top: '0px',
    left: '50%',
    marginLeft: -arrowBaseLength / 2 + 'px',
    width: arrowBaseLength + 'px',
    height: arrowPointingLength + 'px',
    backgroundImage: 'url(' + arrowTop + ')'
  };
  return style;
});

const TooltipArrowBottom = styled(TooltipArrow)((props) => {
  let style = {
    bottom: '0px',
    left: '50%',
    marginLeft: -arrowBaseLength / 2 + 'px',
    width: arrowBaseLength + 'px',
    height: arrowPointingLength + 'px',
    backgroundImage: 'url(' + arrowBottom + ')'
  };
  return style;
});

const TooltipBackdrop = styled.div(() => {
  let style = {
    display: 'block',
    minWidth: '100px',
    maxWidth: '270px',
    fontSize: '12px',
    letterSpacing: '0.5px',
    lineHeight: '18px',
    fontWeight: '300',
    borderRadius: '4px',
    backgroundColor: black,
    color: white,
    // this padding insets the text
    paddingTop: '7px',
    paddingBottom: '9px'
  };
  if (isRtl) {
    style.textAlign = 'right';
    style.direction = rtlDir;
    style.paddingLeft = '10px';
    style.paddingRight = '12px';
  } else {
    style.textAlign = 'left';
    style.direction = ltrDir;
    style.paddingLeft = '12px';
    style.paddingRight = '10px';
  }
  return style;
});

class Tooltip extends PureComponent {
  tipDiv = null;
  tippedElementRect = null;

  componentDidMount = () => {
    isRtl = this.props.isInterfaceRtl;
    const tippedElemNode = ReactDOM.findDOMNode(this.props.tippedElement);
    if (!tippedElemNode) {
      return;
    }
    this.tippedElementRect = tippedElemNode.getBoundingClientRect();

    // the tooltip is drawn inside a div which is appended to the body
    //  which takes it out of all the zIndex hierarchy etc
    this.tipDiv = document.createElement('div');
    document.body.appendChild(this.tipDiv);

    const hiddenTip = (
      <TooltipDiv tipPosition={this.props.tipPosition} fadeTip={true}>
        <TooltipBackdrop>{this.props.tipText}</TooltipBackdrop>
      </TooltipDiv>
    );

    // render it once to measure, then again to show it
    ReactDOM.render(hiddenTip, this.tipDiv, () => {
      this.renderMeasuredTip(false);
    });
  };

  renderMeasuredTip = (fading) => {
    let tipArrow = null;
    switch (this.props.tipPosition) {
      case 'top':
        tipArrow = <TooltipArrowBottom />;
        break;
      case 'right':
        tipArrow = <TooltipArrowLeft />;
        break;
      case 'bottom':
        tipArrow = <TooltipArrowTop />;
        break;
      default:
      case 'left':
        tipArrow = <TooltipArrowRight />;
        break;
    }

    const tooltipElement = this.tipDiv.children[0];
    const tipRect = tooltipElement.getBoundingClientRect();
    const shownTip = (
      <TooltipDiv
        tipPosition={this.props.tipPosition}
        tippedRect={this.tippedElementRect}
        tipRect={tipRect}
        fadeTip={fading}
      >
        {tipArrow}
        <TooltipBackdrop>{this.props.tipText}</TooltipBackdrop>
      </TooltipDiv>
    );
    ReactDOM.render(shownTip, this.tipDiv);
  };

  componentWillUnmount = () => {
    this.renderMeasuredTip(true);
    window.setTimeout(() => {
      // remove tooltip div from body after it fades out
      document.body.removeChild(this.tipDiv);
    }, 1000 * fadeSeconds);
  };

  // this is only to keep react happy, the tooltip is not part of its DOM
  render = () => {
    return null;
  };
}

Tooltip.propTypes = {
  // the text
  tipText: PropTypes.string.isRequired,
  // 'top' 'right' 'bottom' or 'left'
  tipPosition: PropTypes.string.isRequired,
  // the component being annotated by the tip
  tippedElement: PropTypes.object.isRequired
};

const mapStateToProps = (state, ownprops) => ({
  isInterfaceRtl: appSelectors.isInterfaceRtl(state)
});

const wrapActions = {};
export const TooltipContainer = connect(
  mapStateToProps,
  wrapActions
)(Tooltip);



// WEBPACK FOOTER //
// ./src/modules/app/util/Tooltip.jsx